import { useState } from 'react';
import { Server, ChevronRight, Globe } from 'lucide-react';
import { clsx } from 'clsx';
import EncompassTransactionInspector from './EncompassTransactionInspector';
import EncompassForceStatus from './EncompassForceStatus';
import EncompassDocuments from './EncompassDocuments';
import EncompassLoanViewer from './EncompassLoanViewer';
import MeridianLinkLoanViewer from './MeridianLinkLoanViewer';
import MeridianLinkForceUpdate from './MeridianLinkForceUpdate';
import MeridianLinkDocuments from './MeridianLinkDocuments';
import MeridianLinkDeferredMonitor from './MeridianLinkDeferredMonitor';

type LosType = 'encompass' | 'meridianlink' | 'unknown';

function detectLos(originator: string): LosType {
  if (originator?.includes('encompass')) return 'encompass';
  if (originator?.includes('meridianlink')) return 'meridianlink';
  return 'unknown';
}

// ── Encompass section definitions ──

interface SectionDef {
  key: string;
  label: string;
  description: string;
}

const ENCOMPASS_SECTIONS: SectionDef[] = [
  { key: 'transaction', label: 'Transaction Inspector', description: 'View EPC transaction status, events, and resources' },
  { key: 'force-status', label: 'Force Status', description: 'Manually update the transaction response status' },
  { key: 'documents', label: 'Documents', description: 'List and upload documents for this transaction' },
  { key: 'loan', label: 'Loan Data', description: 'View Encompass loan information' },
];

const MERIDIANLINK_SECTIONS: SectionDef[] = [
  { key: 'loan', label: 'Loan Viewer', description: 'View loan details, applicants, and property info' },
  { key: 'force-update', label: 'Push Data', description: 'Push data updates to MeridianLink loan' },
  { key: 'documents', label: 'Documents', description: 'List and upload documents for this loan' },
  { key: 'deferred', label: 'Deferred Monitor', description: 'Monitor deferred update polling status' },
];

// ── Extract LOS-specific IDs from order payload ──

function extractEncompassIds(order: any): { transactionId: string; loanId: string; correlationId: string } {
  // Try to get these from the order's request payload or order fields
  const req = order?.payloads?.request || {};
  const resp = order?.payloads?.response || {};

  return {
    transactionId: req.transactionId || resp.transactionId || order?.vis?.urn || '',
    loanId: req.loanId || resp.loanId || req.loan?.id || '',
    correlationId: order?.urn || order?.uuid || '',
  };
}

function extractMeridianLinkIds(order: any): { loanNumber: string; appNumber: string } {
  const req = order?.payloads?.request || {};
  const resp = order?.payloads?.response || {};

  return {
    loanNumber: req.loanNumber || resp.loanNumber || req.loan?.loanNumber || '',
    appNumber: req.appNumber || resp.appNumber || req.loan?.loanNumber || '',
  };
}

// ── Main component ──

interface Props {
  order: any;
}

export default function LosOperationsPanel({ order }: Props) {
  const losType = detectLos(order?.originator || '');
  const [activeSection, setActiveSection] = useState<string | null>(null);

  if (losType === 'unknown') {
    return (
      <div className="text-center py-8">
        <Globe className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
        <p className="text-sm text-gray-500 dark:text-gray-400">
          This order does not originate from a recognized LOS
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
          LOS operations are available for Encompass and MeridianLink orders.
        </p>
        {order?.originator && (
          <p className="text-xs text-gray-400 mt-2">
            Originator: <span className="font-mono">{order.originator}</span>
          </p>
        )}
      </div>
    );
  }

  const sections = losType === 'encompass' ? ENCOMPASS_SECTIONS : MERIDIANLINK_SECTIONS;
  const losLabel = losType === 'encompass' ? 'Encompass' : 'MeridianLink';

  const encompassIds = losType === 'encompass' ? extractEncompassIds(order) : null;
  const meridianLinkIds = losType === 'meridianlink' ? extractMeridianLinkIds(order) : null;

  const renderSection = () => {
    if (!activeSection) return null;

    if (losType === 'encompass' && encompassIds) {
      switch (activeSection) {
        case 'transaction':
          return <EncompassTransactionInspector transactionId={encompassIds.transactionId} />;
        case 'force-status':
          return <EncompassForceStatus transactionId={encompassIds.transactionId} />;
        case 'documents':
          return <EncompassDocuments transactionId={encompassIds.transactionId} />;
        case 'loan':
          return <EncompassLoanViewer loanId={encompassIds.loanId} />;
        default:
          return null;
      }
    }

    if (losType === 'meridianlink' && meridianLinkIds) {
      switch (activeSection) {
        case 'loan':
          return <MeridianLinkLoanViewer loanNumber={meridianLinkIds.loanNumber} />;
        case 'force-update':
          return <MeridianLinkForceUpdate loanNumber={meridianLinkIds.loanNumber} />;
        case 'documents':
          return <MeridianLinkDocuments loanNumber={meridianLinkIds.loanNumber} appNumber={meridianLinkIds.appNumber} />;
        case 'deferred':
          return <MeridianLinkDeferredMonitor loanNumber={meridianLinkIds.loanNumber} />;
        default:
          return null;
      }
    }

    return null;
  };

  return (
    <div className="space-y-4">
      {/* LOS identifier */}
      <div className={clsx(
        'flex items-center gap-2 p-3 rounded-lg border',
        losType === 'encompass'
          ? 'bg-indigo-50 dark:bg-indigo-950/20 border-indigo-200 dark:border-indigo-500/20'
          : 'bg-teal-50 dark:bg-teal-950/20 border-teal-200 dark:border-teal-500/20',
      )}>
        <Server className={clsx('h-4 w-4', losType === 'encompass' ? 'text-indigo-500' : 'text-teal-500')} />
        <div>
          <p className={clsx('text-xs font-medium', losType === 'encompass' ? 'text-indigo-700 dark:text-indigo-300' : 'text-teal-700 dark:text-teal-300')}>
            {losLabel} Integration
          </p>
          <p className="text-[10px] text-gray-500">
            Originator: <span className="font-mono">{order.originator}</span>
          </p>
        </div>
      </div>

      {/* LOS-specific IDs */}
      {encompassIds && (
        <div className="grid grid-cols-2 gap-3">
          {encompassIds.transactionId && (
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">Transaction ID</p>
              <p className="text-xs text-gray-800 dark:text-gray-200 font-mono mt-0.5 truncate" title={encompassIds.transactionId}>
                {encompassIds.transactionId}
              </p>
            </div>
          )}
          {encompassIds.loanId && (
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">Loan ID</p>
              <p className="text-xs text-gray-800 dark:text-gray-200 font-mono mt-0.5 truncate" title={encompassIds.loanId}>
                {encompassIds.loanId}
              </p>
            </div>
          )}
        </div>
      )}

      {meridianLinkIds && (
        <div className="grid grid-cols-2 gap-3">
          {meridianLinkIds.loanNumber && (
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">Loan Number</p>
              <p className="text-xs text-gray-800 dark:text-gray-200 font-mono mt-0.5">{meridianLinkIds.loanNumber}</p>
            </div>
          )}
          {meridianLinkIds.appNumber && meridianLinkIds.appNumber !== meridianLinkIds.loanNumber && (
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">App Number</p>
              <p className="text-xs text-gray-800 dark:text-gray-200 font-mono mt-0.5">{meridianLinkIds.appNumber}</p>
            </div>
          )}
        </div>
      )}

      {/* Section buttons */}
      <div className="space-y-1">
        {sections.map((section) => (
          <button
            key={section.key}
            onClick={() => setActiveSection(activeSection === section.key ? null : section.key)}
            className={clsx(
              'w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors',
              activeSection === section.key
                ? losType === 'encompass'
                  ? 'border-indigo-300 bg-indigo-50 dark:bg-indigo-900/20 dark:border-indigo-500/30'
                  : 'border-teal-300 bg-teal-50 dark:bg-teal-900/20 dark:border-teal-500/30'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800/30',
            )}
          >
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-gray-800 dark:text-gray-200">{section.label}</p>
              <p className="text-[10px] text-gray-500 mt-0.5">{section.description}</p>
            </div>
            <ChevronRight
              className={clsx(
                'h-4 w-4 text-gray-400 transition-transform shrink-0',
                activeSection === section.key && 'rotate-90',
              )}
            />
          </button>
        ))}
      </div>

      {/* Active section content */}
      {activeSection && (
        <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/50">
          {renderSection()}
        </div>
      )}
    </div>
  );
}
